'use client';
import { useEffect, useState } from 'react';
import { Arrow2, Location } from '@ui/Icons';
import useGooglePlaceAutocomplete from '@/hooks/useGooglePlace';
import LocationInput from '@/components/shared/LocationInput';
import { useLocationStore } from '@/stores/place';
import useStore from '@/stores/useStore';
import { cn } from '@/lib/utils';
import { getFormatedAddress } from '@/lib/helpers/base';
import { MapAddress } from '@/types/address';
import useAppLocale from '@/hooks/useAppLocale';
import { useTranslations } from 'next-intl';
import { storeFront } from '@/lib/utils/routeLinks';

interface Props {
  pathName?: string;
  lat?: string | null;
  lng?: string | null;
  pageType?: string | null;
  rootClasses?: string | null;
}

const LocationSelector = (props: Props) => {
  const { rootClasses }: Props = props;

  const currentLocation = useStore(useLocationStore, (state) => state.address);

  const { setCurrentLocation } = useLocationStore();

  const t = useTranslations();

  const [openLocationMenuBtn, setOpenLocationMenuBtn] =
    useState<boolean>(false);
  const { lang, countryCode } = useAppLocale();
  const { place, setDefaultValue }: any = useGooglePlaceAutocomplete(
    null,
    'google-place',
    {
      fields: [
        'formatted_address',
        'place_id',
        'geometry',
        'address_components',
      ],
      strictBounds: false,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      types: [
        'street_address',
        'route',
        'postal_code',
        'locality',
        'sublocality',
      ],
      componentRestrictions: { country: countryCode.toLocaleLowerCase() },
    }
  );
  const handleOpenLocationMenuBtn = () => {
    setOpenLocationMenuBtn(!openLocationMenuBtn);
  };

  useEffect(() => {
    if (!place && currentLocation?.formatted_address) {
      setDefaultValue(currentLocation.formatted_address);
    }
  }, [currentLocation, setDefaultValue, place]);

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const address: MapAddress = getFormatedAddress(place);
    const lat = address.location?.lat ? address.location.lat : '';
    const lng = address.location?.lng ? address.location.lng : '';
    const url = `${storeFront.restaurants[
      lang as 'de' | 'en'
    ]()}?lat=${lat}&lng=${lng}`;

    setCurrentLocation(address);

    window.location.replace(url);
  };

  return (
    <div className={cn('relative hidden md:ml-[60px] lg:block', rootClasses)}>
      <p className="mb-2 text-sm font-[700] text-paragraph">
        {t('common.from')}:
      </p>
      <div
        className="flex cursor-pointer items-center"
        onClick={handleOpenLocationMenuBtn}
      >
        <div
          className={`${
            openLocationMenuBtn ? 'text-primary-700' : 'text-neutral-400'
          }`}
        >
          <Location />
        </div>
        <span className="ml-1 inline-block w-[145px] truncate text-sm text-paragraph xl:min-w-[185px] xl:max-w-[320px]">
          {currentLocation ? currentLocation.formatted_address : ''}
        </span>
        <div
          className={`ml-[6px] flex h-[20px] w-[20px] transform items-center justify-center transition-transform duration-300 ease-in-out ${
            openLocationMenuBtn
              ? 'rotate-0 text-primary-700'
              : 'rotate-180 text-neutral-400'
          }`}
        >
          <svg
            width="16"
            height="8"
            viewBox="0 0 16 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.8337 6.68519L9.03147 1.9219C8.46434 1.35937 7.53631 1.35937 6.96918 1.9219L2.16699 6.68518"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      <div
        className={`absolute left-0 right-0 top-[100%] z-30 mt-[16px] ${
          openLocationMenuBtn ? '' : 'hidden'
        } w-[632px] overflow-hidden rounded-[10px] bg-white p-[20px] shadow-[0px_10px_30px_0px_rgba(0,0,0,0.10)]`}
      >
        <button
          className="absolute right-4 top-4 flex h-[24px] w-[24px] items-center justify-center rounded-full border-2 border-neutral-400 text-xs font-[700] leading-none text-neutral-400 hover:border-primary-700 hover:text-primary-700"
          onClick={() => setOpenLocationMenuBtn(false)}
        >
          &#x2715;
        </button>
        <h4 className="mb-5 text-h8 font-[700] text-title">
          {t('common.change_location')}
        </h4>
        <div className="relative">
          <form
            className="flex flex-col items-start justify-start space-y-3 sm:flex-row sm:items-center sm:space-y-0"
            onSubmit={handleSubmit}
          >
            <LocationInput
              wrapClassName="lg:!w-[490px]"
              inputClassName="!py-[14px]"
              iconClassName="!top-[13px]"
            />
            <button
              type="submit"
              className="rounded-full border-2 border-primary-700 bg-primary-700 p-[11px_28px] text-white transition-colors hover:bg-white hover:text-primary-700 disabled:opacity-90 disabled:hover:bg-primary-700 disabled:hover:text-white sm:ml-4"
            >
              <Arrow2 />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LocationSelector;
